import React, { useState, useEffect } from 'react';
import { Button, Input, Textarea, Select, Checkbox } from '@/components/ui';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { UnifiedRole } from '@/services/unifiedRBACService';
import { toast } from 'react-hot-toast';
import { RolePermissionsModuleView } from './RolePermissionsModuleView';

interface RoleEditFormProps {
  role?: UnifiedRole | null;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const RoleEditForm: React.FC<RoleEditFormProps> = ({ role, onSave, onCancel }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  
  // Check if user is platform admin
  const isPlatformAdmin = user?.role === 'Platform Administrator' || 
                          user?.role === 'PLATFORM_ADMIN' ||
                          user?.role === 'tmos_admin';
  
  const [formData, setFormData] = useState({
    role_name: '',
    display_name: '',
    description: '',
    role_type: 'TENANT' as 'PLATFORM' | 'TENANT',
    scope_type: 'TENANT' as 'PLATFORM' | 'TENANT',
    is_active: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (role) {
      setFormData({
        role_name: role.role_name,
        display_name: role.display_name,
        description: role.description || '',
        role_type: role.role_type,
        scope_type: role.scope_type,
        is_active: role.is_active
      });
    }
  }, [role]);

  const handleInputChange = (field: string, value: any) => {
    // Extract string value if it's an object (for Select components)
    let cleanValue = value;
    
    // Handle different value types
    if (value === null || value === undefined) {
      cleanValue = '';
    } else if (typeof value === 'object') {
      // Check if it's a Select option object with a value property
      if ('value' in value && typeof value.value !== 'object') {
        cleanValue = value.value;
      } else if ('target' in value && value.target) {
        // Handle event objects
        cleanValue = value.target.value;
      } else {
        // If it's a DOM element or complex object, try to extract a primitive
        // This should not happen, but handle it gracefully
        cleanValue = String(value);
      }
    } else {
      // Primitive value (string, number, boolean) - use as is
      cleanValue = value;
    }
    
    // Validation: Tenant admins cannot set role_type or scope_type to PLATFORM
    if (!isPlatformAdmin) {
      if (field === 'role_type' && cleanValue === 'PLATFORM') {
        toast.error('Tenant administrators cannot create platform type roles.');
        return;
      }
      if (field === 'scope_type' && cleanValue === 'PLATFORM') {
        toast.error('Tenant administrators cannot create platform scope roles.');
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: cleanValue
    }));
  };





  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Helper function to safely extract primitive values
      const getPrimitiveValue = (value: any): any => {
        if (value === null || value === undefined) {
          return value;
        }
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return value;
        }
        if (typeof value === 'object') {
          // If it's an object with a value property, extract it
          if ('value' in value) {
            return getPrimitiveValue(value.value);
          }
          // If it's a DOM element or complex object, convert to string
          if (value instanceof HTMLElement || value.nodeType !== undefined) {
            return String(value);
          }
          // Try to stringify and parse back (will fail if circular)
          try {
            return JSON.parse(JSON.stringify(value));
          } catch {
            return String(value);
          }
        }
        return value;
      };
      
      // Save role data - create completely independent object with only primitive values
      const roleData = {
        role_name: getPrimitiveValue(formData.role_name),
        display_name: getPrimitiveValue(formData.display_name),
        description: getPrimitiveValue(formData.description),
        role_type: getPrimitiveValue(formData.role_type),
        scope_type: getPrimitiveValue(formData.scope_type),
        is_active: getPrimitiveValue(formData.is_active)
      };
      
      // Validate that all values are primitives
      const hasInvalidValue = Object.values(roleData).some(val => 
        val !== null && val !== undefined && typeof val === 'object'
      );
      
      if (hasInvalidValue) {
        throw new Error('Form contains invalid data. Please refresh the page and try again.');
      }
      
      await onSave(roleData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg w-full h-full">
      <h2 className="text-2xl font-bold mb-6">
        {role ? t('actions.editRole') : t('actions.createRole')}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Role Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('roles.displayName')}
            </label>
            <Input
              type="text"
              value={formData.display_name}
              onChange={(e) => handleInputChange('display_name', e.target.value)}
              required
              placeholder={t('roles.displayNamePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('roles.internalName')}
            </label>
            <Input
              type="text"
              value={formData.role_name}
              onChange={(e) => handleInputChange('role_name', e.target.value)}
              required
              placeholder={t('roles.internalNamePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('roles.roleType')}
            </label>
            <Select
              value={formData.role_type}
              onChange={(value) => handleInputChange('role_type', value)}
              options={
                isPlatformAdmin
                  ? [
                      { value: 'PLATFORM', label: t('roles.platform') },
                      { value: 'TENANT', label: t('roles.tenant') }
                    ]
                  : [
                      { value: 'TENANT', label: t('roles.tenant') }
                    ]
              }
              disabled={!isPlatformAdmin} // Disable for tenant admins (they can only create tenant roles)
            />
            {!isPlatformAdmin && (
              <p className="mt-1 text-xs text-gray-500">
                {t('roles.tenantCanOnlyCreateTenantRoles') || 'Tenant administrators can only create tenant roles.'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('roles.scopeType')}
            </label>
            <Select
              value={formData.scope_type}
              onChange={(value) => handleInputChange('scope_type', value)}
              options={
                isPlatformAdmin
                  ? [
                      { value: 'PLATFORM', label: t('roles.platformScope') },
                      { value: 'TENANT', label: t('roles.tenantScope') }
                    ]
                  : [
                      { value: 'TENANT', label: t('roles.tenantScope') }
                    ]
              }
              disabled={!isPlatformAdmin} // Disable for tenant admins (they can only create tenant roles)
            />
            {!isPlatformAdmin && (
              <p className="mt-1 text-xs text-gray-500">
                {t('roles.tenantCanOnlyCreateTenantScope') || 'Tenant administrators can only create tenant scope roles.'}
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('roles.description')}
          </label>
          <Textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder={t('roles.descriptionPlaceholder')}
            rows={3}
          />
        </div>

        <div>
          <label className="flex items-center">
            <Checkbox
              checked={formData.is_active}
                             onChange={(checked: boolean) => handleInputChange('is_active', checked)}
            />
            <span className="ml-2 text-sm font-medium text-gray-700">
              {t('roles.active')}
            </span>
          </label>
        </div>

        {/* Permissions - module-level and screen-level (only when editing existing role) */}
        {role && <RolePermissionsModuleView roleId={role.id} />}

        {/* Form Actions - only show when creating a new role */}
        {!role && (
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? t('common.saving') : t('actions.create')}
            </Button>
          </div>
        )}
        
        {/* When editing, only show back button */}
        {role && (
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              {t('actions.backToList') || 'Back to list'}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export { RoleEditForm };
export default RoleEditForm;
